import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BadgeCheck, AlertTriangle, Loader2 } from "lucide-react";

export default function PublicCertificate() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/verify/${code}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Certificato non trovato");
      } finally { setLoading(false); }
    })();
  }, [code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md p-8 text-center rounded-2xl">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-400" />
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Certificato non trovato</h2>
        <p className="text-sm text-slate-500 mb-4">{error}</p>
        <Button onClick={() => navigate("/registro")} data-testid="back-registry-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Torna al registro
        </Button>
      </Card>
    </div>
  );

  const isValid = data.valid;
  const expDate = data.expires_at ? new Date(data.expires_at).toLocaleDateString("it-IT") : "—";
  const issueDate = data.issued_at ? new Date(data.issued_at).toLocaleDateString("it-IT") : "—";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Button variant="ghost" onClick={() => navigate("/registro")} className="mb-4" data-testid="back-registry-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Torna al registro
        </Button>
        <Card className="rounded-3xl overflow-hidden border-slate-200 shadow-soft" data-testid="public-cert-detail">
          {/* Hero */}
          <div className={`p-8 ${isValid ? "bg-gradient-to-r from-emerald-600 to-viva-700" : "bg-gradient-to-r from-slate-600 to-slate-800"} text-white`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] opacity-80 mb-2">Certificato VIVA ESG</div>
                <h1 className="font-heading text-3xl font-bold mb-2">{data.organization.ragione_sociale}</h1>
                <div className="text-sm opacity-90">
                  P.IVA {data.organization.piva || "—"} · {data.organization.sede_legale || ""}
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full font-bold text-sm ${isValid ? "bg-white/20 text-white" : "bg-red-500/30 text-white"}`}>
                {isValid ? "✓ VALIDO" : data.status?.toUpperCase()}
              </div>
            </div>
          </div>
          {/* Body */}
          <div className="p-8 grid grid-cols-2 md:grid-cols-3 gap-6">
            <Field label="Codice certificato" value={data.certificate_code} mono />
            <Field label="Banda VIVA" value={data.band || "—"} />
            <Field label="Rating finale" value={data.final_rating ? `${data.final_rating.toFixed(1)} / 100` : "—"} />
            <Field label="Anno valutazione" value={data.year} />
            <Field label="Profilo" value={data.profile} />
            <Field label="Cluster settoriale" value={data.cluster_code} />
            {data.snapshot_frozen === false && (
              <div className="col-span-2 md:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="public-snapshot-warning">
                Valori provvisori: lo snapshot di questo certificato non è ancora stato congelato.
              </div>
            )}
            <Field label="Data emissione" value={issueDate} />
            <Field label="Scadenza" value={expDate} highlight={!isValid} />
            <Field label="Stato" value={data.status} />
          </div>
          {isValid && (
            <div className="px-8 pb-8">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm flex gap-3">
                <BadgeCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-emerald-900">
                  Questo certificato è <strong>valido</strong> alla data odierna ed è stato emesso secondo
                  il processo descritto dal Disciplinare Tecnico DT-RTG-01 § 6.5. Per chiarimenti contatta
                  <a href="mailto:tecnico@vivaesg.it" className="underline ml-1">tecnico@vivaesg.it</a>.
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div className={`${mono ? "font-mono text-sm" : "font-semibold"} ${highlight ? "text-red-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
